import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { LoadingAnimation, sizes } from '@uag/react-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlacementSolutionRow } from 'app/modules/wallforming/components/setting-container/automated-placement/PlacementSolutionRow';
import { usePlacementSolutionHandler } from 'app/modules/wallforming/components/setting-container/hooks/usePlacementSolutionHandler';

const SOLUTION_ROW_GAP = 4;
const SOLUTION_ROW_OVERSCAN = 4;

const getEstimatedSolutionRowHeight = (placementInfoCount: number) => Math.max(40, placementInfoCount * 32) + 2;

interface PlacementSolutionListProps {
    isLoading: boolean;
}

export const PlacementSolutionList = ({ isLoading }: PlacementSolutionListProps) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lastSelectedSolutionRef = useRef<number | null>(null);
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [viewportHeight, setViewportHeight] = useState<number>(600);
    const {
        solutions,
        selectedSolution,
        showPlacementPreview,
        hasAppliedSolution,
        isSolutionApplied,
        solutionItemRefs,
        handleOnApplySolution,
        handleOnReset,
        handleOnDoubleClick,
        handleOnPointerDown,
        setShowPlacementPreview
    } = usePlacementSolutionHandler();

    const solutionsCount = useMemo(() => solutions?.length ?? 0, [solutions]);
    const rowOffsets = useMemo(() => {
        const offsets: number[] = [];
        let offset = 0;

        for (const solution of solutions ?? []) {
            offsets.push(offset);
            offset += getEstimatedSolutionRowHeight(solution.placementInfos.length) + SOLUTION_ROW_GAP;
        }

        return { offsets, totalHeight: Math.max(0, offset - SOLUTION_ROW_GAP) };
    }, [solutions]);

    const isSelectedSolutionApplied = useMemo(() => {
        const solution = selectedSolution === null ? undefined : solutions?.[selectedSolution];
        return solution === undefined || isSolutionApplied(solution);
    }, [isSolutionApplied, selectedSolution, solutions]);

    const handleOnRegisterRef = useCallback(
        (element: HTMLDivElement | null, index: number) => {
            if (element) {
                solutionItemRefs.current.set(index, element);
            } else {
                solutionItemRefs.current.delete(index);
            }
        },
        [solutionItemRefs]
    );

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const updateViewportHeight = () => setViewportHeight(container.clientHeight);
        updateViewportHeight();
        const observer = new ResizeObserver(updateViewportHeight);
        observer.observe(container);

        return () => observer.disconnect();
    }, [isLoading, solutionsCount]);

    const visibleRange = useMemo(() => {
        let low = 0;
        let high = solutionsCount;
        while (low < high) {
            const middle = (low + high) >>> 1;
            const rowHeight = getEstimatedSolutionRowHeight(solutions?.[middle]?.placementInfos.length ?? 0);
            if (rowOffsets.offsets[middle] + rowHeight < scrollTop) {
                low = middle + 1;
            } else {
                high = middle;
            }
        }

        const start = Math.max(0, low - SOLUTION_ROW_OVERSCAN);
        const bottom = scrollTop + viewportHeight;
        let end = start;
        while (end < solutionsCount && rowOffsets.offsets[end] < bottom) {
            end += 1;
        }

        return {
            start,
            end: Math.min(solutionsCount, end + SOLUTION_ROW_OVERSCAN)
        };
    }, [rowOffsets.offsets, scrollTop, solutions, solutionsCount, viewportHeight]);

    useEffect(() => {
        if (lastSelectedSolutionRef.current === selectedSolution) {
            return;
        }
        lastSelectedSolutionRef.current = selectedSolution;

        if (
            selectedSolution === null ||
            selectedSolution < 0 ||
            selectedSolution >= solutionsCount ||
            (selectedSolution >= visibleRange.start && selectedSolution < visibleRange.end)
        ) {
            return;
        }

        const nextScrollTop = rowOffsets.offsets[selectedSolution];
        if (nextScrollTop !== undefined && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = nextScrollTop;
            setScrollTop(nextScrollTop);
        }
    }, [rowOffsets.offsets, selectedSolution, solutionsCount, visibleRange]);

    if (!solutions) {
        return null;
    }

    return (
        <section className="flex h-full min-h-0 min-w-0 flex-col gap-1 overflow-hidden">
            <div className="mb-1 flex min-w-30 shrink-0 items-center justify-between gap-2 text-xs font-semibold text-text-gray-dark">
                <span className="min-w-0 shrink-0 whitespace-nowrap text-xs font-semibold uppercase">
                    {t('labelSolutions')} ({solutionsCount})
                </span>
                <Tooltip label={showPlacementPreview ? t('labelHidePreview') : t('labelShowPreview')}>
                    <ActionIcon
                        aria-label={showPlacementPreview ? t('labelHidePreview') : t('labelShowPreview')}
                        className="bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto action-icon-medium"
                        disabled={selectedSolution === null}
                        onPointerDown={() => setShowPlacementPreview(!showPlacementPreview)}
                    >
                        {showPlacementPreview ? 'visibility' : 'visibility_off'}
                    </ActionIcon>
                </Tooltip>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {isLoading && <LoadingAnimation text={t('loading')} />}

                {!isLoading && solutionsCount > 0 && (
                    <div
                        ref={scrollContainerRef}
                        className="min-h-0 flex-1 overflow-auto pr-1 pb-1"
                        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
                    >
                        <div className="relative w-full" style={{ height: rowOffsets.totalHeight }}>
                            {solutions.slice(visibleRange.start, visibleRange.end).map((solution, offsetIndex) => {
                                const index = visibleRange.start + offsetIndex;
                                const isApplied = isSolutionApplied(solution);

                                return (
                                    <div
                                        key={`${solution.articleNumber}-${index}`}
                                        className="absolute left-0 right-0"
                                        style={{ top: rowOffsets.offsets[index] }}
                                    >
                                        <PlacementSolutionRow
                                            handleOnDoubleClick={handleOnDoubleClick}
                                            handleOnRegisterRef={handleOnRegisterRef}
                                            handleOnSelect={handleOnPointerDown}
                                            hasAppliedSolution={hasAppliedSolution}
                                            index={index}
                                            isApplied={isApplied}
                                            isSelected={selectedSolution === index}
                                            solution={solution}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!isLoading && solutionsCount === 0 && (
                    <span className="text-[13px] text-text-light">{t('labelNoSolutions')}</span>
                )}
            </div>

            <div className="mt-1 flex min-w-40 shrink-0 flex-wrap items-center gap-2">
                <Button
                    disabled={selectedSolution === null || !hasAppliedSolution}
                    size={sizes.button.small}
                    variant="secondary"
                    onPointerDown={handleOnReset}
                >
                    {t('reset')}
                </Button>

                <Button
                    disabled={isSelectedSolutionApplied}
                    id="automated-corner-apply"
                    size={sizes.button.small}
                    onPointerDown={() => handleOnApplySolution(selectedSolution)}
                >
                    {t('apply')}
                </Button>
            </div>
        </section>
    );
};
